<template>
  <div class="root-container">
    <div class="table-container">
      <!-- 操作栏 -->
      <div class="action-bar">
        <div class="action-bar-info">
          <span style="font-size:13px;color:var(--text-200);">修改完 SEO 后点击按钮更新静态页面，蜘蛛即可抓取最新内容</span>
        </div>
        <el-button type="success" icon="el-icon-refresh" :loading="regenerating" @click="handleRegenerate" v-if="isDesktop">
          更新 SEO 页面
        </el-button>
        <van-button type="success" icon="replay" :loading="regenerating" size="small" @click="handleRegenerate" v-else>
          更新 SEO 页面
        </van-button>
      </div>

      <!-- 内容区域 -->
      <div class="content-container">
        <!-- 电脑端 -->
        <div class="table-wrapper" v-if="isDesktop">
          <el-table :data="groupedList" style="width: 100%" tooltip-effect="dark" empty-text="暂无数据"
            class="desktop-table" :height="tableHeight" row-key="page_key">
            <el-table-column label="页面" align="left" width="130px" fixed>
              <template slot-scope="scope">
                <div style="font-weight:500;">{{ scope.row.page_name }}</div>
                <div style="font-size:12px;color:var(--text-200);">{{ scope.row.page_key }}</div>
              </template>
            </el-table-column>
            <el-table-column label="中文 SEO" align="left" min-width="250px">
              <template slot-scope="scope">
                <seo-cell :item="scope.row.zh" @edit="handleEdit" />
              </template>
            </el-table-column>
            <el-table-column label="English SEO" align="left" min-width="250px">
              <template slot-scope="scope">
                <seo-cell :item="scope.row.en" @edit="handleEdit" />
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 手机端卡片 -->
        <div v-else class="mobile-cards">
          <div v-if="groupedList.length === 0" class="mobile-empty">暂无数据</div>
          <div v-for="group in groupedList" :key="group.page_key" class="mobile-card">
            <div class="card-header">
              <div class="card-title">
                <h3>{{ group.page_name }}</h3>
                <span style="font-size:12px;color:var(--text-200);">{{ group.page_key }}</span>
              </div>
            </div>
            <!-- 中文 -->
            <div class="seo-lang-section" v-if="group.zh">
              <div class="seo-lang-header">
                <el-tag size="mini">中文</el-tag>
                <van-button type="primary" size="mini" plain @click="handleEdit(group.zh)">编辑</van-button>
              </div>
              <div class="seo-lang-body">
                <div class="seo-lang-row" v-if="group.zh.title">
                  <span class="seo-lang-label">标题</span>
                  <span class="seo-lang-value">{{ group.zh.title }}</span>
                </div>
                <div class="seo-lang-row" v-if="group.zh.description">
                  <span class="seo-lang-label">描述</span>
                  <span class="seo-lang-value">{{ group.zh.description }}</span>
                </div>
                <div class="seo-lang-row" v-if="group.zh.keywords">
                  <span class="seo-lang-label">关键词</span>
                  <div class="seo-lang-tags">
                    <van-tag v-for="(kw, idx) in group.zh.keywords.split(',')" :key="idx" plain type="primary" size="mini">
                      {{ kw.trim() }}
                    </van-tag>
                  </div>
                </div>
              </div>
            </div>
            <!-- 英文 -->
            <div class="seo-lang-section" v-if="group.en">
              <div class="seo-lang-header">
                <el-tag size="mini" type="success">EN</el-tag>
                <van-button type="primary" size="mini" plain @click="handleEdit(group.en)">Edit</van-button>
              </div>
              <div class="seo-lang-body">
                <div class="seo-lang-row" v-if="group.en.title">
                  <span class="seo-lang-label">Title</span>
                  <span class="seo-lang-value">{{ group.en.title }}</span>
                </div>
                <div class="seo-lang-row" v-if="group.en.description">
                  <span class="seo-lang-label">Desc</span>
                  <span class="seo-lang-value">{{ group.en.description }}</span>
                </div>
                <div class="seo-lang-row" v-if="group.en.keywords">
                  <span class="seo-lang-label">Keys</span>
                  <div class="seo-lang-tags">
                    <van-tag v-for="(kw, idx) in group.en.keywords.split(',')" :key="idx" plain type="success" size="mini">
                      {{ kw.trim() }}
                    </van-tag>
                  </div>
                </div>
              </div>
            </div>
            <!-- 都没有 -->
            <div v-if="!group.zh && !group.en" class="seo-empty-section">
              <span style="color:var(--text-200);font-size:13px;">暂无SEO数据</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 编辑对话框 - PC端 -->
    <template v-if="isDesktop">
      <el-drawer title="编辑 SEO 设置" :visible.sync="dialogVisible" size="500px" :before-close="handleClose">
        <div class="drawer-content">
          <div class="seo-page-badge">
            <span class="seo-page-name">{{ editForm.page_name }}</span>
            <el-tag size="mini" :type="editForm.language_code === 'zh' ? '' : 'success'">
              {{ editForm.language_code === 'zh' ? '中文' : 'English' }}
            </el-tag>
          </div>
          <el-form :model="editForm" label-width="100px" ref="elForm">
            <el-form-item label="SEO 标题">
              <el-input v-model="editForm.title" placeholder="请输入 SEO 标题" maxlength="200" show-word-limit />
            </el-form-item>
            <el-form-item label="SEO 描述">
              <el-input v-model="editForm.description" type="textarea" :rows="3" placeholder="请输入 SEO 描述" maxlength="500" show-word-limit />
            </el-form-item>
            <el-form-item label="关键词">
              <el-input v-model="editForm.keywords" placeholder="多个关键词用英文逗号分隔" maxlength="500" />
              <div style="margin-top:4px;font-size:12px;color:var(--text-200);">提示：多个关键词请用英文逗号分隔，如：谱地科技,新能源,PCB焊接</div>
            </el-form-item>
          </el-form>
          <div class="drawer-footer">
            <el-button @click="dialogVisible = false">取消</el-button>
            <el-button type="primary" :loading="submitting" @click="handleSubmit">保存</el-button>
          </div>
        </div>
      </el-drawer>
    </template>

    <!-- 编辑对话框 - 移动端 -->
    <template v-else>
      <van-popup v-model="dialogVisible" position="bottom" :round="true" :style="{ height: '70%' }" :close-on-click-overlay="false">
        <div class="popup-header">
          <h3>编辑 SEO 设置</h3>
          <van-icon name="cross" @click="handleClosePopup" />
        </div>
        <div class="popup-content">
          <div class="seo-page-badge">
            <span class="seo-page-name">{{ editForm.page_name }}</span>
            <van-tag size="mini" :type="editForm.language_code === 'zh' ? 'primary' : 'success'">
              {{ editForm.language_code === 'zh' ? '中文' : 'EN' }}
            </van-tag>
          </div>
          <van-form ref="vanForm">
            <van-field v-model="editForm.title" label="SEO 标题" placeholder="请输入 SEO 标题" maxlength="200" />
            <van-field v-model="editForm.description" label="SEO 描述" type="textarea" rows="3" placeholder="请输入 SEO 描述" autosize maxlength="500" />
            <van-field v-model="editForm.keywords" label="关键词" placeholder="多个关键词用逗号分隔" maxlength="500" />
            <div style="padding: 4px 16px 8px; font-size: 12px; color: var(--text-200);">
              提示：多个关键词请用英文逗号分隔
            </div>
          </van-form>
          <div style="padding: 16px;">
            <van-button type="primary" block :loading="submitting" @click="handleSubmit">保存</van-button>
          </div>
        </div>
      </van-popup>
    </template>
  </div>
</template>

<script>
import { getSeoSettings, updateSeoSetting, regenerateSeoHtml } from '@/api/website'
import { Dialog } from 'vant'

// 内联子组件：SEO 单元格（PC 端表格内）
var SeoCell = {
  name: 'SeoCell',
  props: {
    item: {
      type: Object,
      default: function() { return null }
    }
  },
  render: function(h) {
    var item = this.item
    if (!item) {
      return h('div', { class: 'seo-cell seo-cell-none' }, [
        h('span', { style: { color: 'var(--text-200)', fontSize: '12px' } }, '暂无')
      ])
    }
    var children = []
    var labelStyle = {
      flexShrink: '0',
      width: '52px',
      fontSize: '11px',
      fontWeight: '600',
      color: 'white',
      background: 'var(--primary)',
      padding: '2px 6px',
      borderRadius: '4px',
      marginRight: '10px',
      textAlign: 'center'
    }
    // 标题行
    if (item.title) {
      children.push(h('div', { class: 'seo-cell-row' }, [
        h('span', { style: labelStyle }, '标题:'),
        h('span', { class: 'seo-cell-value' }, item.title)
      ]))
    }
    // 描述行
    if (item.description) {
      children.push(h('div', { class: 'seo-cell-row' }, [
        h('span', { style: labelStyle }, '描述:'),
        h('span', { class: 'seo-cell-value seo-cell-desc' }, item.description)
      ]))
    }
    // 关键词行
    if (item.keywords) {
      var tags = item.keywords.split(',').map(function(kw, idx) {
        return h('el-tag', {
          key: idx,
          props: { size: 'mini', type: 'info' },
          style: { marginRight: '4px' }
        }, kw.trim())
      })
      children.push(h('div', { class: 'seo-cell-row' }, [
        h('span', { style: labelStyle }, '关键词:'),
        h('div', { class: 'seo-cell-tags' }, tags)
      ]))
    }
    // 空状态
    if (!item.title && !item.description && !item.keywords) {
      children.push(h('div', { class: 'seo-cell-empty' }, [
        h('span', { style: { color: 'var(--text-200)', fontSize: '12px' } }, '未填写')
      ]))
    }
    // 编辑按钮（块状）
    children.push(h('el-button', {
      class: 'seo-cell-btn',
      props: { size: 'mini', type: 'primary' },
      on: { click: this.handleEdit }
    }, '编辑'))
    return h('div', { class: 'seo-cell' }, children)
  },
  methods: {
    handleEdit: function() {
      this.$emit('edit', this.item)
    }
  }
}

export default {
  name: 'SeoSettingsPage',
  components: {
    SeoCell: SeoCell
  },
  data() {
    return {
      isDesktop: true,
      tableData: [],
      dialogVisible: false,
      editingId: null,
      submitting: false,
      regenerating: false,
      editForm: {
        page_key: '',
        page_name: '',
        language_code: '',
        title: '',
        description: '',
        keywords: ''
      }
    }
  },
  computed: {
    tableHeight() {
      if (!this.isDesktop) return 'auto'
      // 计算表格高度：减去顶部栏、筛选区域、分页栏和底部的高度
      const headerHeight = 60 // 顶部栏高度
      const searchHeight = this.isFilterOpen ? 200 : 60 // 搜索栏高度（包含筛选面板）
      const paginationHeight = 60 // 分页栏高度
      const footerHeight = 40 // 底部高度
      const padding = 20 // 页面内边距
      return window.innerHeight - headerHeight - searchHeight - footerHeight - padding - 120 // 100是其他元素的额外高度
    },
    groupedList() {
      var map = {}
      var list = this.tableData
      for (var i = 0; i < list.length; i++) {
        var item = list[i]
        var key = item.page_key
        if (!map[key]) {
          map[key] = {
            page_key: key,
            page_name: item.page_name,
            zh: null,
            en: null
          }
        }
        if (item.language_code === 'zh') {
          map[key].zh = item
        } else if (item.language_code === 'en') {
          map[key].en = item
        }
      }
      return Object.values(map)
    }
  },
  mounted() {
    this.checkScreenSize()
    window.addEventListener('resize', this.handleResize)
    this.getTableData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.checkScreenSize()
    },
    checkScreenSize() {
      this.isDesktop = window.innerWidth > 768
    },
    getTableData() {
      var vm = this
      getSeoSettings().then(function(res) {
        if (res.code === 200) {
          vm.tableData = res.data || []
        }
      }).catch(function() {})
    },
    handleEdit(row) {
      this.editingId = row.id
      this.editForm = {
        page_key: row.page_key || '',
        page_name: row.page_name || '',
        language_code: row.language_code || 'zh',
        title: row.title || '',
        description: row.description || '',
        keywords: row.keywords || ''
      }
      this.dialogVisible = true
    },
    handleClose(done) {
      this.$confirm('确认关闭？').then(function() {
        done()
      }).catch(function() {})
    },
    handleClosePopup() {
      var vm = this
      Dialog.confirm({
        title: '提示',
        message: '确认关闭？',
        confirmButtonText: '确定',
        cancelButtonText: '取消'
      }).then(function() {
        vm.dialogVisible = false
      }).catch(function() {})
    },
    handleSubmit() {
      var vm = this
      vm.submitting = true
      var data = {
        title: vm.editForm.title,
        description: vm.editForm.description,
        keywords: vm.editForm.keywords
      }
      updateSeoSetting(vm.editingId, data).then(function(res) {
        if (res.code === 200) {
          vm.$message.success('保存成功')
          vm.dialogVisible = false
          vm.getTableData()
        } else {
          vm.$message.error(res.msg || '保存失败')
        }
      }).catch(function() {
        vm.$message.error('保存失败')
      }).finally(function() {
        vm.submitting = false
      })
    },
    handleRegenerate() {
      var vm = this
      Dialog.confirm({
        title: '更新 SEO 页面',
        message: '将重新生成所有页面的 SEO HTML，此操作可能需要几秒钟，确认继续？',
        confirmButtonText: '确定',
        cancelButtonText: '取消'
      }).then(function() {
        vm.regenerating = true
        regenerateSeoHtml().then(function(res) {
          if (res.code === 200) {
            vm.$message.success('SEO 页面更新成功')
          } else {
            vm.$message.error(res.msg || '更新失败')
          }
        }).catch(function() {
          vm.$message.error('请求失败')
        }).finally(function() {
          vm.regenerating = false
        })
      }).catch(function() {})
    }
  }
}
</script>

<style scoped lang="scss">
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  margin-bottom: 10px;
}

.action-bar-info {
  flex: 1;
}

.table-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* 内容区域 */
.content-container {
  flex: 1;
  height: 100%;
  overflow: hidden;
  padding: 20px 0;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
}

.table-wrapper {
  height: 100%;
  margin-bottom: 0;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  font-size: 14px;
}

.table-actions {
  width: 100%;
  display: inline-flex;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
}

.desktop-table {
  height: 100%;
  margin-bottom: 0;
  font-size: 14px;
}

/* PC 端 SEO 单元格 */
.seo-cell {
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.seo-cell-row {
  display: flex;
  align-items: flex-start;
  line-height: 1.5;
}

.seo-cell-label {
  flex-shrink: 0;
  width: 52px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  background: var(--primary);
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 10px;
  text-align: center;
}

.seo-cell-value {
  flex: 1;
  font-size: 13px;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.seo-cell-desc {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  color: var(--text-200);
  font-size: 12px;
}

.seo-cell-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex: 1;
}

.seo-cell-empty {
  padding: 4px 0;
}

.seo-cell-none {
  display: flex;
  align-items: center;
  min-height: 40px;
}

.seo-cell-btn {
  align-self: flex-start;
  margin-top: 4px !important;
}

/* 手机端卡片 */
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 0;
}

.mobile-card {
  border: 1px solid var(--border);
  border-radius: var(--border-radius-lg);
  padding: 15px;
  background-color: var(--bg-200);
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
}

.mobile-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-light);
}

.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.card-title h3 {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--text);
}

/* 语言区块 */
.seo-lang-section {
  background: var(--bg-100);
  border-radius: var(--border-radius);
  padding: 12px;
  margin-bottom: 10px;
}

.seo-lang-section:last-child {
  margin-bottom: 0;
}

.seo-lang-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.seo-lang-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.seo-lang-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
}

.seo-lang-label {
  flex-shrink: 0;
  color: var(--text-200);
  font-size: 12px;
  width: 40px;
}

.seo-lang-value {
  flex: 1;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.seo-lang-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex: 1;
}

.seo-empty-section {
  padding: 16px 0;
  text-align: center;
}

.mobile-empty {
  text-align: center;
  padding: 40px 0;
  color: var(--text-200);
  font-size: var(--font-size-sm);
}

/* Drawer 内容 */
.drawer-content {
  padding: 20px 24px;
}

.drawer-footer {
  padding: 20px 0;
  text-align: right;
  border-top: 1px solid var(--border);
}

/* SEO 页面标识 */
.seo-page-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--bg-100);
  border-radius: var(--border-radius);
  margin-bottom: 20px;
}

.seo-page-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
}

/* 弹窗 */
.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: var(--background);
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  z-index: 10;
}

.popup-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--text);
}

.popup-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: var(--background);
}

/* 响应式 */
@media (max-width: 768px) {
  .mobile-cards {
    display: flex;
  }

  .content-container {
    padding: 10px 0;
  }
}

@media (min-width: 769px) {
  .desktop-table {
    display: block;
  }

  .mobile-cards {
    display: none;
  }
}
</style>
